import { useCallback, useEffect, useMemo, useState } from "react";
import useAuth from "../../../hooks/useAuth";
import { agencyRoleService } from "../../../services/agencyRole.service";
import "../../../styles/rolesPermissions.css";

const emptyForm = {
  name: "",
  description: "",
  permissions: [],
};

const sortPermissions = (items = []) => [...items].sort((a, b) => a.label.localeCompare(b.label));

export default function RolesPermissions() {
  const { auth } = useAuth();
  const [roles, setRoles] = useState([]);
  const [catalog, setCatalog] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  const [editingRole, setEditingRole] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [managementRole, setManagementRole] = useState(null);
  const [managementUsers, setManagementUsers] = useState([]);
  const [assignableUsers, setAssignableUsers] = useState([]);
  const [assigning, setAssigning] = useState(false);
  const [expandedRoleId, setExpandedRoleId] = useState(null);
  const [selectedAssignUserId, setSelectedAssignUserId] = useState("");
  const [userSearch, setUserSearch] = useState("");

  const token = auth?.token;

  const sortedCatalog = useMemo(
    () => sortPermissions(catalog),
    [catalog]
  );

  const loadData = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    setError("");
    try {
      const [rolesRes, permissionRes] = await Promise.all([
        agencyRoleService.getRoles(token),
        agencyRoleService.getPermissionCatalog(token),
      ]);
      setRoles(Array.isArray(rolesRes?.roles) ? rolesRes.roles : []);
      setCatalog(Array.isArray(permissionRes?.permissions) ? permissionRes.permissions : []);
    } catch (err) {
      setError(err?.response?.data?.message || "Failed to load roles");
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const openCreateModal = () => {
    setEditingRole(null);
    setForm(emptyForm);
    setModalOpen(true);
  };

  const openEditModal = (role) => {
    if (role.isDefault || role.isSystem) return;
    setEditingRole(role);
    setForm({
      name: role.name || "",
      description: role.description || "",
      permissions: role.permissions || [],
    });
    setModalOpen(true);
  };

  const togglePermission = (key) => {
    setForm((prev) => {
      const exists = prev.permissions.includes(key);
      return {
        ...prev,
        permissions: exists
          ? prev.permissions.filter((item) => item !== key)
          : [...prev.permissions, key],
      };
    });
  };

  const selectAll = () => {
    setForm((prev) => ({
      ...prev,
      permissions: sortedCatalog.map((item) => item.key),
    }));
  };

  const clearAll = () => {
    setForm((prev) => ({ ...prev, permissions: [] }));
  };

  const handleSaveRole = async () => {
    if (!token) return;
    const trimmedName = form.name.trim();
    if (!trimmedName) {
      setError("Role name is required.");
      return;
    }
    setSaving(true);
    setError("");

    try {
      if (editingRole) {
        await agencyRoleService.updateRole(editingRole.id || editingRole._id, {
          name: trimmedName,
          description: form.description.trim(),
          permissions: form.permissions,
        }, token);
      } else {
        await agencyRoleService.createRole({
          name: trimmedName,
          description: form.description.trim(),
          permissions: form.permissions,
        }, token);
      }
      setModalOpen(false);
      setForm(emptyForm);
      await loadData();
    } catch (err) {
      setError(err?.response?.data?.message || "Failed to save role");
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteRole = async (role) => {
    if (!token) return;
    const ok = window.confirm(`Delete role "${role.name}"?`);
    if (!ok) return;
    try {
      await agencyRoleService.deleteRole(role.id || role._id, token);
      await loadData();
    } catch (err) {
      setError(err?.response?.data?.message || "Failed to delete role");
    }
  };

  const openUserManagement = async (role) => {
    if (!token) return;
    const roleId = role.id || role._id;
    setManagementRole(role);
    setExpandedRoleId(roleId);
    setSelectedAssignUserId("");
    setUserSearch("");
    try {
      const [roleUsersRes, assignableRes] = await Promise.all([
        agencyRoleService.getRoleUsers(roleId, token),
        agencyRoleService.getAssignableUsers(token),
      ]);
      setManagementUsers(Array.isArray(roleUsersRes?.users) ? roleUsersRes.users : []);
      setAssignableUsers(Array.isArray(assignableRes?.users) ? assignableRes.users : []);
    } catch (err) {
      setError(err?.response?.data?.message || "Failed to load role users");
    }
  };

  const handleAssignUser = async (userId = selectedAssignUserId) => {
    if (!managementRole || !token || !userId) return;
    setAssigning(true);
    try {
      await agencyRoleService.assignUsers(managementRole.id || managementRole._id, [userId], token);
      setSelectedAssignUserId("");
      setUserSearch("");
      await openUserManagement(managementRole);
    } catch (err) {
      setError(err?.response?.data?.message || "Failed to assign user");
    } finally {
      setAssigning(false);
    }
  };

  const handleRemoveUser = async (userId) => {
    if (!managementRole || !token) return;
    try {
      await agencyRoleService.removeUser(managementRole.id || managementRole._id, userId, token);
      await openUserManagement(managementRole);
    } catch (err) {
      setError(err?.response?.data?.message || "Failed to remove user");
    }
  };

  const availableAssignableUsers = assignableUsers.filter(
    (user) =>
      !(managementUsers || []).some(
        (assigned) => (assigned.userId || assigned._id) === (user.userId || user._id)
      )
  );

  const filteredAssignableUsers = availableAssignableUsers.filter((user) => {
    const label = (user.name || user.email || "").toLowerCase();
    return label.includes((userSearch || "").toLowerCase());
  });

  return (
    <div className="content roles-page">
      <div className="roles-page-header">
        <div>
          <h1>Roles & Permissions</h1>
          <p className="muted">Manage custom role permissions for your agency.</p>
        </div>
        <button type="button" className="primary-page-btn" onClick={openCreateModal}>
          + Create Role
        </button>
      </div>

      {error && <p className="error-text">{error}</p>}

      {loading ? (
        <div className="roles-empty-state">
          <p className="muted">Loading roles…</p>
        </div>
      ) : (
        <div className="roles-list">
          {roles.length === 0 ? (
            <div className="roles-empty-state">
              <p className="muted">No custom roles created yet.</p>
            </div>
          ) : (
            roles.map((role) => {
              const roleId = role.id || role._id;
              const isExpanded = expandedRoleId === roleId && managementRole && (managementRole.id || managementRole._id) === roleId;

              return (
                <div key={roleId} className={`role-card ${isExpanded ? "role-card--expanded" : ""}`}>
                  <div className="role-card__header">
                    <div className="role-card__title-group">
                      <span className="role-card__tag">{role.isDefault ? "System Default" : "Role"}</span>
                      <h3>{role.name}</h3>
                    </div>

                    <div className="role-card__actions">
                      {!role.isDefault && !role.isSystem && (
                        <button type="button" className="secondary-page-btn" onClick={() => openEditModal(role)}>
                          Edit
                        </button>
                      )}
                      <button type="button" className="secondary-page-btn" onClick={() => openUserManagement(role)}>
                        {isExpanded ? "Manage Users" : "Manage Users"}
                      </button>
                      {!role.isDefault && !role.isSystem && (
                        <button type="button" className="secondary-page-btn" onClick={() => handleDeleteRole(role)}>
                          Delete
                        </button>
                      )}
                    </div>
                  </div>

                  <div className="role-card__meta">
                    <div className="role-meta-item">
                      <span>Description</span>
                      <p>{role.description || "No description"}</p>
                    </div>
                    <div className="role-meta-item">
                      <span>Users</span>
                      <p>{role.usersCount ?? 0}</p>
                    </div>
                    <div className="role-meta-item">
                      <span>Permissions</span>
                      <p>{role.permissionsCount ?? role.permissions?.length ?? 0}</p>
                    </div>
                  </div>

                  {isExpanded && (
                    <div className="role-details-panel">
                      <div className="role-details-panel__header">
                        <h4>Role Details</h4>
                      </div>

                      <div className="role-details-panel__body">
                        <div className="role-details-section">
                          <h5>Assigned Users</h5>
                          <div className="assigned-user-list">
                            {managementUsers.length === 0 ? (
                              <div className="role-user-empty">No users assigned yet.</div>
                            ) : (
                              managementUsers.map((user) => (
                                <div key={user.userId || user._id} className="role-user-row">
                                  <span className="role-user-row__name">{user.name || user.email}</span>
                                  <button
                                    type="button"
                                    className="secondary-page-btn role-user-row__remove"
                                    onClick={() => handleRemoveUser(user.userId || user._id)}
                                  >
                                    Remove
                                  </button>
                                </div>
                              ))
                            )}
                          </div>
                        </div>

                        <div className="role-details-section">
                          <h5>Assign User</h5>
                          <div className="assign-user-row">
                            <input
                              type="text"
                              value={userSearch}
                              onChange={(e) => setUserSearch(e.target.value)}
                              placeholder="Search or select a user..."
                              list="assignable-users-list"
                              onKeyDown={(e) => {
                                if (e.key === "Enter") {
                                  const nextUser = filteredAssignableUsers[0];
                                  if (nextUser) {
                                    setSelectedAssignUserId(nextUser.userId || nextUser._id);
                                    handleAssignUser(nextUser.userId || nextUser._id);
                                  }
                                }
                              }}
                            />
                            <datalist id="assignable-users-list">
                              {filteredAssignableUsers.map((user) => (
                                <option key={user.userId || user._id} value={user.name || user.email} />
                              ))}
                            </datalist>
                            <button
                              type="button"
                              className="primary-page-btn"
                              disabled={assigning || !selectedAssignUserId}
                              onClick={() => handleAssignUser(selectedAssignUserId)}
                            >
                              {assigning ? "Assigning..." : "Assign"}
                            </button>
                          </div>

                          <div className="assign-user-options">
                            {filteredAssignableUsers.length === 0 ? (
                              <p className="muted small-text">No assignable users found.</p>
                            ) : (
                              filteredAssignableUsers.map((user) => {
                                const userValue = user.userId || user._id;
                                return (
                                  <button
                                    key={userValue}
                                    type="button"
                                    className={`assign-user-option ${selectedAssignUserId === userValue ? "is-selected" : ""}`}
                                    onClick={() => {
                                      setSelectedAssignUserId(userValue);
                                      handleAssignUser(userValue);
                                    }}
                                    disabled={assigning}
                                  >
                                    {user.name || user.email}
                                  </button>
                                );
                              })
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>
      )}

      {modalOpen && (
        <div className="modal-backdrop" onClick={() => setModalOpen(false)}>
          <div className="modal-card roles-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <div>
                <span className="roles-modal__eyebrow">Role management</span>
                <h3>{editingRole ? "Edit role" : "Create a new role"}</h3>
                <p className="roles-modal__intro">Define what this team member can access across your agency.</p>
              </div>
              <button type="button" className="icon-close" onClick={() => setModalOpen(false)} aria-label="Close">
                ×
              </button>
            </div>

            <div className="roles-modal__section">
              <div className="roles-modal__section-heading">
                <span className="roles-modal__step">01</span>
                <div>
                  <h4>Role details</h4>
                  <p>Give this role a clear name and purpose.</p>
                </div>
              </div>
              <div className="roles-modal__fields">
                <div className="form-group">
                  <label htmlFor="role-name">Role name <span aria-hidden="true">*</span></label>
                  <input
                    id="role-name"
                    value={form.name}
                    onChange={(e) => setForm((prev) => ({ ...prev, name: e.target.value }))}
                    placeholder="e.g. Field Agent"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="role-description">Description <span className="optional-label">Optional</span></label>
                  <textarea
                    id="role-description"
                    value={form.description}
                    onChange={(e) => setForm((prev) => ({ ...prev, description: e.target.value }))}
                    rows={3}
                    placeholder="What is this role responsible for?"
                  />
                </div>
              </div>
            </div>

            <div className="roles-modal__section roles-modal__permissions-section">
              <div className="roles-modal__section-heading">
                <span className="roles-modal__step">02</span>
                <div>
                  <h4>Access permissions</h4>
                  <p>Choose the areas this role can view and manage.</p>
                </div>
              </div>
              <div className="roles-permissions-toolbar">
                <span className="roles-permissions-count"><strong>{form.permissions.length}</strong> of {sortedCatalog.length} selected</span>
                <div>
                  <button type="button" className="text-action-btn" onClick={selectAll}>Select all</button>
                  <button type="button" className="text-action-btn text-action-btn--muted" onClick={clearAll}>Clear all</button>
                </div>
              </div>

              <div className="permission-grid">
                {sortedCatalog.map((permission) => {
                  const isSelected = form.permissions.includes(permission.key);
                  return (
                    <label key={permission.key} className={`permission-item ${isSelected ? "is-selected" : ""}`}>
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => togglePermission(permission.key)}
                      />
                      <span>{permission.label}</span>
                    </label>
                  );
                })}
              </div>
            </div>

            <div className="modal-actions">
              <button type="button" className="secondary-page-btn" onClick={() => setModalOpen(false)}>
                Cancel
              </button>
              <button type="button" className="primary-page-btn" onClick={handleSaveRole} disabled={saving}>
                {saving ? "Saving..." : editingRole ? "Save Changes" : "Create Role"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
